import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { getAllEvents } from '../../services/eventService';
import { exportBookings } from '../../services/bookingService';
import toast from 'react-hot-toast';

const ExportModal = ({ show, onHide }) => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (show) {
      fetchEvents();
    }
  }, [show]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getAllEvents();
      const allEvents = response.data?.data || [];
      
      // Filter events to show only future events
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);
      
      const futureEvents = allEvents.filter(event => {
        if (!event.date) return false;
        const eventDate = new Date(event.date);
        return eventDate >= currentDate;
      });
      
      setEvents(futureEvents);
    } catch (err) {
      setError('Failed to fetch events');
      toast.error('Failed to fetch events');
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!selectedEvent) {
      toast.error('Please select an event');
      return;
    }

    try {
      setExporting(true);
      setError('');
      
      const response = await exportBookings(selectedEvent);
      
      // Create blob and download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      // Find the selected event name for the filename
      const eventName = events.find(event => event._id === selectedEvent)?.title || 'event';
      const fileName = `${eventName}_bookings_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Export completed successfully!');
      onHide();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Export failed';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Export error:', err);
    } finally {
      setExporting(false);
    }
  };

  const handleClose = () => {
    setSelectedEvent('');
    setError('');
    setExporting(false);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faDownload} className="me-2" />
          Export Bookings
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Select Event *</Form.Label>
            {loading ? (
              <div className="text-center py-3">
                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                Loading events...
              </div>
            ) : (
              <Form.Select 
                value={selectedEvent} 
                onChange={(e) => setSelectedEvent(e.target.value)}
                disabled={exporting}
              >
                <option value="">Choose an event...</option>
                {events.map(event => (
                  <option key={event._id} value={event._id}>
                    {event.title} - {new Date(event.date).toLocaleDateString('en-GB', { 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </option>
                ))}
              </Form.Select>
            )}
            <Form.Text className="text-muted">
              Select an event to export its booking data to Excel format
            </Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={exporting}>
          Cancel
        </Button>
        <Button 
          variant="success" 
          onClick={handleExport}
          disabled={!selectedEvent || loading || exporting}
        >
          {exporting ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
              Exporting...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faDownload} className="me-2" />
              Download Excel
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ExportModal;
